import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useSSEStream } from './useSSEStream';

const DEBOUNCE_MS = 400;

interface UseCalendarRealtimeOptions {
  organizationId: string | undefined;
  enabled?: boolean;
  getAccessToken?: () => Promise<string | null>;
}

// FLOSEAI-594 AC5: mirrors useBoardRealtime/useTeamRealtime exactly — same
// SSE plumbing (useSSEStream, which already owns reconnect/backoff and
// survives the connection dropping), same debounce-then-invalidate shape.
//
// The payload is intentionally never parsed for routing: every message on
// this org-scoped channel means "something in this calendar changed", so
// any message is reason enough to invalidate — same as useBoardRealtime and
// useTeamRealtime, which also ignore the message body.
//
// Invalidates the 3-element prefix ['calendar', 'events', organizationId],
// not a longer key. useCalendarEvents.ts currently queries exactly that key
// (verified by reading it, not assumed); if a future range-scoped key
// (['calendar','events', organizationId, rangeKey]) lands, React Query's
// default prefix matching (exact: false) still catches it — and because
// invalidateQueries only forces a refetch for currently-mounted (active)
// queries, a range the user isn't viewing is marked stale, not force-
// refetched, so it never fights whatever range is currently on screen.
export function useCalendarRealtime({
  organizationId,
  enabled = true,
  getAccessToken,
}: UseCalendarRealtimeOptions) {
  const queryClient = useQueryClient();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMessage = useCallback(
    (_data: string) => {
      if (!organizationId) return;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['calendar', 'events', organizationId] });
        debounceTimerRef.current = null;
      }, DEBOUNCE_MS);
    },
    [queryClient, organizationId],
  );

  useEffect(
    () => () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    },
    [],
  );

  const { isConnected } = useSSEStream({
    path: 'realtime/calendar/stream',
    organizationId,
    enabled,
    getAccessToken,
    onMessage: handleMessage,
  });

  return { isConnected };
}
